/**
 * Теги товара в колонке окна: чипы и, при праве Product:UPDATE, выбор тегов тем
 * же поповером, что в каталогах. Словарь тегов общий с каталогом макетов, поэтому
 * грузится лениво — при первом открытии выбора, а не вместе с окном.
 */

import React, { useCallback, useState } from 'react'
import { Plus } from 'lucide-react'
import { TagPicker } from '../../../../components/catalog/TagPicker'
import { tagChipClass, type CatalogTag } from '../../../../components/catalog/tags'
import { cn } from '../../../../lib/cn'
import { useToastStore } from '../../../../stores/toastStore'
import { productsV2API } from '../../api'

interface ProductTagsProps {
  article: string
  tags: CatalogTag[]
  /** Нет права на правку: теги только показываются. */
  readOnly?: boolean
  onChange: (tags: CatalogTag[]) => void
}

export const ProductTags: React.FC<ProductTagsProps> = ({ article, tags, readOnly, onChange }) => {
  const showToast = useToastStore((state) => state.showToast)
  const [dictionary, setDictionary] = useState<CatalogTag[] | null>(null)

  const ensureDictionary = useCallback(() => {
    if (dictionary !== null) return
    void productsV2API
      .getTags()
      .then(setDictionary)
      .catch(() => {
        setDictionary([])
        showToast('Не удалось загрузить список тегов', 'error')
      })
  }, [dictionary, showToast])

  const save = async (next: CatalogTag[]) => {
    try {
      await productsV2API.setProductTags(article, next.map((tag) => tag.id))
      onChange(next)
    } catch (error: any) {
      showToast(error?.response?.data?.error?.message || error?.message || 'Не удалось сохранить теги', 'error')
    }
  }

  if (readOnly && tags.length === 0) return null

  return (
    <div data-testid="product-tags">
      <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-500">
        Теги
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {tags.map((tag) => (
          <span
            key={tag.id}
            className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium', tagChipClass(tag.color))}
          >
            {tag.value}
          </span>
        ))}
        {tags.length === 0 && <span className="text-xs text-gray-400 dark:text-zinc-500">нет данных</span>}
        {!readOnly && (
          <TagPicker
            tags={dictionary ?? []}
            selectedIds={tags.map((tag) => tag.id)}
            onToggle={(tag, checked) => {
              const next = checked ? [...tags.filter((item) => item.id !== tag.id), tag] : tags.filter((item) => item.id !== tag.id)
              void save(next)
            }}
            onCreate={async (value, color) => {
              try {
                const created = await productsV2API.createTag(value, color)
                setDictionary((current) => [...(current ?? []), created])
                return created
              } catch {
                showToast('Не удалось создать тег', 'error')
                return null
              }
            }}
            trigger={(open) => (
              <button
                type="button"
                onClick={() => {
                  ensureDictionary()
                  open()
                }}
                aria-label="Изменить теги"
                title="Изменить теги"
                className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-dashed border-gray-300 text-gray-400 hover:border-indigo-400 hover:text-indigo-600 dark:border-zinc-600 dark:text-zinc-500"
              >
                <Plus className="h-3 w-3" />
              </button>
            )}
          />
        )}
      </div>
    </div>
  )
}
